import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { formatXof } from '../utils/format';
import { confirmDialog } from '../utils/confirm';

const STOCK_TYPES = ['Entrée', 'Sortie', 'Inventaire', 'Stock critique', 'Affectation', 'Commande'];
const CATEGORIES = ['Fournitures scolaires', 'Papeterie', 'Matériel pédagogique', "Produits d'entretien", 'Informatique', 'Mobilier', 'Autre'];
const EMPTY = {
  designation: '', category: '', stockType: 'Entrée', quantity: '', unitPrice: '',
  fournisseurId: '', supplier: '', reference: '', location: '',
  date: new Date().toISOString().slice(0, 10), notes: '',
};

export default function Stocks() {
  const [rows, setRows] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  function load() {
    api.get('/registry/stocks').then((d) =>
      setRows((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })))
    ).catch(() => {});
  }

  useEffect(() => {
    load();
    api.get('/registry/suppliers').then((d) =>
      setFournisseurs((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })))
    ).catch(() => {});
  }, []);

  async function del(id) {
    if (!(await confirmDialog({ title: 'Supprimer', message: 'Supprimer ce mouvement ? Action irréversible.', confirmLabel: 'Supprimer', danger: true }))) return;
    try { await api.del(`/registry/stocks/${id}`); load(); } catch (e) { setError(e.message); }
  }

  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    const matchQ = !q || (r.designation || '').toLowerCase().includes(q) || (r.category || '').toLowerCase().includes(q);
    const matchT = !typeFilter || r.stockType === typeFilter;
    return matchQ && matchT;
  });

  const totalEntrees = rows.filter((r) => r.stockType === 'Entrée').reduce((s, r) => s + Number(r.quantity || 0), 0);
  const totalSorties = rows.filter((r) => r.stockType === 'Sortie').reduce((s, r) => s + Number(r.quantity || 0), 0);
  const valeurStock = rows.reduce((s, r) => s + (Number(r.quantity || 0) * Number(r.unitPrice || 0)), 0);

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi"><span>Mouvements</span><strong>{rows.length}</strong></div>
        <div className="kpi green"><span>Total entrées</span><strong>{totalEntrees}</strong></div>
        <div className="kpi"><span>Total sorties</span><strong>{totalSorties}</strong></div>
        <div className="kpi gold"><span>Valeur estimée</span><strong>{formatXof(valeurStock)}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Stocks & fournitures</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="search-input" placeholder="Désignation, catégorie…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ width: 150 }}>
              <option value="">Tous les types</option>
              {STOCK_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <button className="btn btn-sm" onClick={() => setModal({ ...EMPTY })}>+ Mouvement</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {filtered.length === 0 ? <div className="empty">Aucun mouvement de stock.</div> : (
            <div className="table-wrap"><table>
              <thead>
                <tr>
                  <th>Désignation</th><th>Catégorie</th><th>Type</th><th>Date</th>
                  <th className="num">Qté</th><th className="num">P.U (XOF)</th>
                  <th>Fournisseur</th><th>Emplacement</th><th></th>
                </tr>
              </thead>
              <tbody>{filtered.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.designation || '—'}</b></td>
                  <td>{r.category || '—'}</td>
                  <td><span className={`badge ${r.stockType === 'Entrée' ? 'green' : r.stockType === 'Sortie' ? 'red' : 'navy'}`}>{r.stockType}</span></td>
                  <td className="mono">{r.date ? new Date(r.date).toLocaleDateString('fr-FR') : '—'}</td>
                  <td className="num"><b>{r.quantity}</b></td>
                  <td className="num">{r.unitPrice ? formatXof(r.unitPrice) : '—'}</td>
                  <td>{r.supplier || '—'}</td>
                  <td>{r.location || '—'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn-ghost btn-sm" onClick={() => setView(r)}>Fiche</button>
                    <button className="btn-ghost btn-sm" onClick={() => setModal(r)}>Modifier</button>
                    <button className="link-danger" style={{ fontSize: '0.75rem' }} onClick={() => del(r.id)}>Supprimer</button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {modal && <StockModal initial={modal} fournisseurs={fournisseurs} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {view && <StockView item={view} onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} />}
    </>
  );
}

function StockView({ item, onClose, onEdit }) {
  const R = (l, v) => v != null && v !== '' ? (
    <div className="view-row"><span className="view-label">{l}</span><span className="view-value">{String(v)}</span></div>
  ) : null;
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>Fiche mouvement</h2>
        <div className="view-grid">
          {R('Désignation', item.designation)}
          {R('Catégorie', item.category)}
          {R('Type', item.stockType)}
          {R('Date', item.date ? new Date(item.date).toLocaleDateString('fr-FR') : null)}
          {R('Quantité', item.quantity)}
          {R('Prix unitaire', item.unitPrice ? formatXof(item.unitPrice) : null)}
          {R('Valeur totale', item.quantity && item.unitPrice ? formatXof(Number(item.quantity) * Number(item.unitPrice)) : null)}
          {R('Fournisseur', item.supplier)}
          {R('Référence', item.reference)}
          {R('Emplacement', item.location)}
          {R('Notes', item.notes)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function StockModal({ initial, fournisseurs, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  function onFournisseurChange(e) {
    const id = e.target.value;
    const four = fournisseurs.find((f) => f.id === id);
    setF((p) => ({ ...p, fournisseurId: id, supplier: four ? four.supplierName : '' }));
  }

  async function save() {
    if (!f.designation || !f.quantity) { setError('Désignation et quantité sont obligatoires.'); return; }
    setSaving(true);
    try {
      const payload = { data: f, label: f.designation, searchText: `${f.designation} ${f.category || ''} ${f.supplier || ''}`.trim(), refDate: f.date || undefined };
      if (isEdit) await api.patch(`/registry/stocks/${initial.id}`, payload);
      else await api.post('/registry/stocks', payload);
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Modifier le mouvement' : 'Nouveau mouvement de stock'}</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="fieldset-title">Identification</div>
        <div className="grid-3">
          <div className="field"><label>Désignation *</label><input value={f.designation} onChange={set('designation')} /></div>
          <div className="field"><label>Catégorie</label>
            <select value={f.category} onChange={set('category')}>
              <option value="">—</option>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="field"><label>Type de mouvement</label>
            <select value={f.stockType} onChange={set('stockType')}>
              {STOCK_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="field"><label>Date</label><input type="date" value={f.date} onChange={set('date')} /></div>
          <div className="field"><label>Quantité *</label><input type="number" min="1" value={f.quantity} onChange={set('quantity')} /></div>
          <div className="field"><label>Prix unitaire (XOF)</label><input type="number" min="0" value={f.unitPrice} onChange={set('unitPrice')} /></div>
        </div>
        <div className="fieldset-title">Logistique</div>
        <div className="grid-3">
          <div className="field"><label>Fournisseur</label>
            {fournisseurs.length > 0 ? (
              <>
                <select value={f.fournisseurId || ''} onChange={onFournisseurChange}>
                  <option value="">— Sélectionner —</option>
                  {fournisseurs.map((four) => <option key={four.id} value={four.id}>{four.supplierName}</option>)}
                  <option value="__autre__">Autre (saisie libre)</option>
                </select>
                {f.fournisseurId === '__autre__' && (
                  <input style={{ marginTop: 4 }} value={f.supplier || ''} onChange={set('supplier')} placeholder="Nom du fournisseur" />
                )}
              </>
            ) : (
              <input value={f.supplier || ''} onChange={set('supplier')} placeholder="Nom du fournisseur" />
            )}
          </div>
          <div className="field"><label>Référence</label><input value={f.reference} onChange={set('reference')} /></div>
          <div className="field"><label>Emplacement</label><input value={f.location} onChange={set('location')} /></div>
        </div>
        <div className="field"><label>Notes</label><textarea value={f.notes} onChange={set('notes')} rows={2} /></div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
